import React, { useState, useEffect } from "react";
import { Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { verifyLoginOtp, resetLoginFlag } from "../../../slices/auth/login/thunk";
import { reset_login_flag } from "../../../slices/auth/login/reducer";
import { showSuccessToast, showErrorToast } from "../../../helpers/toast_helper";
import appConfig from "../../../config";
import withRouter from "../../../Components/Common/withRouter";
import DashboardHeader from "../../../Components/Common/DashboardHeader";
import AuthLeftPanel from "../../../Components/Common/AuthLeftPanel";
import OTPInput from "../../../Components/Common/OTPInput";
import AuthFormButton from "../../../Components/Common/AuthFormButton";
import AuthLink from "../../../Components/Common/AuthLink";

const LoginOTP = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '']);

  const [phoneData] = useState({
    phoneno: sessionStorage.getItem("phoneNumber") || '',
    dialcode: sessionStorage.getItem("dialcode") || appConfig.country.defaultDialCode,
    countrycode: sessionStorage.getItem("countrycode") || appConfig.country.defaultCountryCode,
  });

  const selectLoginState = (state: any) => state.Login;
  const selectLoginData = createSelector(selectLoginState, (login) => ({
    loading: login.loading,
    successMsg: login.loginSuccessMsg,
    errorMsg: login.errorMsg,
    isAuthenticated: login.isAuthenticated,
  }));

  const { loading, successMsg, errorMsg, isAuthenticated } = useSelector(selectLoginData);

  useEffect(() => {
    if (!phoneData.phoneno) {
      navigate("/login");
    }
  }, [phoneData.phoneno, navigate]);

  useEffect(() => {
    if (successMsg && isAuthenticated) {
      showSuccessToast("User Logged In Successfully", "login-otp-success");
      setLoader(false);
      sessionStorage.removeItem("phoneNumber");
      sessionStorage.removeItem("dialcode");
      sessionStorage.removeItem("countrycode");
      dispatch(reset_login_flag());
      setTimeout(() => {
        // Redirect back to the page user wanted before login (e.g. /services/cart)
        const redirectPath = sessionStorage.getItem("postLoginRedirect") || "/explore-courses";
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate("/explore-courses");
        }
        sessionStorage.removeItem("postLoginRedirect");
      }, 1500);
    }
  }, [successMsg, isAuthenticated, navigate, dispatch]);

  useEffect(() => {
    if (errorMsg) {
      showErrorToast(errorMsg, "login-otp-error");
      setLoader(false);
      dispatch(reset_login_flag());
    }
  }, [errorMsg, dispatch]);

  const otpValidation = useFormik({
    enableReinitialize: true,
    initialValues: { otp: otp.join('') },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please enter OTP")
        .length(5, "Invalid OTP")
        .matches(/^[0-9]{5}$/, "OTP must contain exactly 5 numbers"),
    }),
    onSubmit: async () => {
      setLoader(true);
      try {
        await dispatch(verifyLoginOtp({
          phoneno: phoneData.phoneno,
          dialcode: phoneData.dialcode,
          countrycode: phoneData.countrycode,
          otp: otp.join(''),
        }));
      } catch (error: any) {
        console.error("Login OTP verification error:", error);
        setLoader(false);
      }
    },
  });

  document.title = "AMANAT - Login OTP";

  return (
    <div className="auth-container">
      <DashboardHeader />
      <div className="auth-main-content">
        <div className="auth-panel-container">
          <AuthLeftPanel
            title="Login"
            description="Welcome back to AMANAT. Enter the OTP sent to your mobile number."
          />
          <div className="auth-right-panel">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                otpValidation.handleSubmit();
                return false;
              }}
              className="auth-form"
            >
              <OTPInput
                value={otp}
                onChange={setOtp}
                error={otpValidation.errors.otp}
                touched={otpValidation.touched.otp}
                onComplete={(otpString) => {
                  otpValidation.setFieldValue('otp', otpString);
                }}
              />
              <div className="auth-form-group">
                <AuthFormButton
                  loading={loader || loading}
                  type="submit"
                  onClick={() => {
                    otpValidation.setFieldValue('otp', otp.join(''));
                    otpValidation.handleSubmit();
                  }}
                >
                  Continue
                </AuthFormButton>
              </div>
            </Form>
            <AuthLink to="/login" text="Back to" linkText="Login" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(LoginOTP);

